import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { AuthActions, AuthFacade } from '@lyceo/shared/auth/data-access';
import { isDefined } from '@lyceo/shared/util';
import { TtFeatureFlagsService } from '@techniek-team/feature-flags';
import { FEEDBACK_MODAL } from '@lyceo/shared/feedback/util';
import { IonicComponentRef } from '@lyceo/shared/util-models';
import { MoreMenuActions, TopBarActions, UserActions } from './user.actions';
import { ModalController } from '@ionic/angular/standalone';
import { TranslocoService } from '@jsverse/transloco';
import { UserService } from '../../user.service';
import { PupilContextFacade } from '@lyceo/shared/pupil-context/data-access';
import { TaskCountActions } from '@lyceo/shared/task-count/data-access';
import { Router } from '@angular/router';

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly userService: UserService = inject(UserService);
  private readonly ttFeatureFlagsService = inject(TtFeatureFlagsService);
  private readonly authFacade: AuthFacade = inject(AuthFacade);
  private readonly feedbackModal = inject<IonicComponentRef>(FEEDBACK_MODAL);
  private readonly translocoService: TranslocoService =
    inject(TranslocoService);
  private readonly modalController: ModalController = inject(ModalController);
  private readonly pupilContextFacade: PupilContextFacade =
    inject(PupilContextFacade);
  private readonly router: Router = inject(Router);

  public loadPersonId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loadUserProfileSuccess),
      switchMap(() =>
        this.userService.getPersonId().pipe(
          map(({ id }) => UserActions.loadPersonIdSuccess({ personId: id })),
          catchError((error) => of(UserActions.loadPersonIdFailure({ error }))),
        ),
      ),
    );
  });

  public setPupilContext = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(UserActions.loadPersonIdSuccess),
        tap((action) =>
          this.pupilContextFacade.setPupilContext({
            homiPersonId: action.personId,
          }),
        ),
      );
    },
    { dispatch: false },
  );

  public setFeatureFlagContext$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.loadUserProfileSuccess),
        switchMap(() =>
          this.ttFeatureFlagsService.isInitialized.pipe(
            filter((initialized) => initialized),
          ),
        ),
        map(() => this.authFacade.userEmail()),
        filter(isDefined),
        tap((email) => {
          this.ttFeatureFlagsService.updateContext({
            userId: email,
          });
        }),
      );
    },
    { dispatch: false },
  );

  public showFeedbackModal$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TopBarActions.feedbackClicked, MoreMenuActions.feedbackClicked),
        tap(async () => {
          const modal: HTMLIonModalElement = await this.modalController.create({
            component: this.feedbackModal,
            componentProps: {
              modalTitle: this.translocoService.translate(
                'feedback.modal_title',
              ),
              modalBodyText: this.translocoService.translate(
                'feedback.modal_body',
              ),
              modalButtonText: this.translocoService.translate(
                'feedback.modal_button',
              ),
            },
            backdropDismiss: true,
            cssClass: 'auto-height-modal feedback-modal',
          });
          await modal.present();
        }),
      );
    },
    { dispatch: false },
  );

  public taskCountClicked$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TaskCountActions.taskCountClicked),
        map(() => this.pupilContextFacade.homiPersonId()),
        filter(isDefined),
        map((personId) => {
          this.router.navigate(['/pupils', personId, 'plan']);
        }),
      );
    },
    { dispatch: false },
  );
}
